import React, { useState, useEffect, useCallback } from 'react';
import reviewApi from '../../services/reviewApi';
import './Reviews.css';

const ReviewDialog = ({ isOpen, onClose, isLoggedIn, user }) => {
    // State for reviews data
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // State for filters
    const [filters, setFilters] = useState({
        rating: '',
        search: '',
        sort: 'newest'
    });

    // State for user info (logged in users)
    const [userInfo, setUserInfo] = useState(null);

    // State for form
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        title: '',
        comment: '',
        rating: 5
    });
    const [formLoading, setFormLoading] = useState(false);
    const [formSuccess, setFormSuccess] = useState(false);
    const [formError, setFormError] = useState(null);

    // Fetch reviews
    const fetchReviews = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                page: currentPage,
                limit: 10,
                sort: filters.sort
            };

            if (filters.rating) params.rating = filters.rating;
            if (filters.search) params.search = filters.search;

            console.log('Fetching reviews with params:', params);
            const response = await reviewApi.getApprovedReviews(params);
            console.log('Reviews API response:', response);

            // Handle response format: { success: true, data: { reviews: [], pagination: {} } }
            if (response.success && response.data) {
                setReviews(response.data.reviews || []);
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.total_pages || 1);
                }
            } else if (response.success && response.reviews) {
                // Alternative format: { success: true, reviews: [], pagination: {} }
                setReviews(response.reviews || []);
                if (response.pagination) {
                    setTotalPages(response.pagination.total_pages || 1);
                }
            } else if (response.status === 'success' && response.data) {
                // Legacy format
                setReviews(response.data.reviews || []);
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.total_pages || 1);
                }
            }
        } catch (err) {
            setError('Failed to load reviews. Please try again.');
            console.error('Error fetching reviews:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, filters]);

    // Fetch stats
    const fetchStats = useCallback(async () => {
        try {
            const response = await reviewApi.getReviewStats();
            if (response.status === 'success' && response.data) {
                setStats(response.data);
            }
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    }, []);

    // Fetch user info for logged-in users
    const fetchUserInfo = useCallback(async () => {
        if (!isLoggedIn) return;

        try {
            const response = await reviewApi.getUserInfo();
            if (response.status === 'success' && response.data) {
                setUserInfo(response.data);
                if (response.data.user) {
                    setFormData(prev => ({
                        ...prev,
                        name: response.data.user.name || '',
                        email: response.data.user.email || ''
                    }));
                }
            }
        } catch (err) {
            console.error('Error fetching user info:', err);
        }
    }, [isLoggedIn]);

    // Load data when dialog opens
    useEffect(() => {
        if (isOpen) {
            fetchReviews();
            fetchStats();
            fetchUserInfo();
        }
    }, [isOpen, fetchReviews, fetchStats, fetchUserInfo]);

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    // Handle form input changes
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handle star rating click
    const handleRatingClick = (rating) => {
        setFormData(prev => ({ ...prev, rating }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        setFormError(null);

        try {
            const submitData = {
                title: formData.title,
                comment: formData.comment,
                rating: parseInt(formData.rating),
                status: 'approved' // Auto-approve reviews
            };

            // Add name/email only for guest users
            if (!isLoggedIn) {
                submitData.name = formData.name;
                submitData.email = formData.email;
            }

            const response = await reviewApi.createReview(submitData);

            if (response.status === 'success') {
                setFormSuccess(true);
                setFormData({
                    name: userInfo?.user?.name || '',
                    email: userInfo?.user?.email || '',
                    title: '',
                    comment: '',
                    rating: 5
                });

                // Refresh reviews after submission
                setTimeout(() => {
                    fetchReviews();
                    fetchStats();
                }, 1000);
            }
        } catch (err) {
            setFormError(err.message || 'Failed to submit review. Please try again.');
        } finally {
            setFormLoading(false);
        }
    };

    // Render star rating
    const renderStars = (rating, interactive = false, size = 'md') => {
        return (
            <div className={`stars-container stars-${size}`}>
                {[1, 2, 3, 4, 5].map(star => (
                    <span
                        key={star}
                        className={`star ${star <= rating ? 'filled' : ''} ${interactive ? 'interactive' : ''}`}
                        onClick={interactive ? () => handleRatingClick(star) : undefined}
                    >
                        ★
                    </span>
                ))}
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="review-dialog-overlay" onClick={onClose}>
            <div className="review-dialog" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="review-dialog-header">
                    <h2>Customer Reviews</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="review-dialog-content">
                    {/* Stats Section */}
                    {stats && (
                        <div className="review-stats">
                            <div className="stats-summary">
                                <div className="average-rating">
                                    <span className="rating-number">{stats.average_rating?.toFixed(1) || '0.0'}</span>
                                    {renderStars(Math.round(stats.average_rating || 0), false, 'lg')}
                                    <span className="total-reviews">{stats.total_reviews || 0} reviews</span>
                                </div>
                                <div className="rating-distribution">
                                    {[5, 4, 3, 2, 1].map(rating => (
                                        <div key={rating} className="rating-bar">
                                            <span className="rating-label">{rating} ★</span>
                                            <div className="bar-container">
                                                <div
                                                    className="bar-fill"
                                                    style={{
                                                        width: `${stats.total_reviews ?
                                                            ((stats.rating_distribution?.[rating] || 0) / stats.total_reviews * 100) : 0}%`
                                                    }}
                                                ></div>
                                            </div>
                                            <span className="rating-count">{stats.rating_distribution?.[rating] || 0}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Filters */}
                    <div className="review-filters">
                        <div className="filter-group">
                            <input
                                type="text"
                                placeholder="Search reviews..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <div className="filter-group">
                            <select
                                value={filters.rating}
                                onChange={(e) => handleFilterChange('rating', e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Ratings</option>
                                <option value="5">5 Stars</option>
                                <option value="4">4 Stars</option>
                                <option value="3">3 Stars</option>
                                <option value="2">2 Stars</option>
                                <option value="1">1 Star</option>
                            </select>
                        </div>
                        <div className="filter-group">
                            <select
                                value={filters.sort}
                                onChange={(e) => handleFilterChange('sort', e.target.value)}
                                className="filter-select"
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="rating_high">Highest Rated</option>
                                <option value="rating_low">Lowest Rated</option>
                            </select>
                        </div>
                        {/* Only show Write Review button for logged-in users */}
                        {isLoggedIn && (
                            <button
                                className="write-review-btn"
                                onClick={() => setShowForm(!showForm)}
                            >
                                {showForm ? 'Hide Form' : '✍️ Write a Review'}
                            </button>
                        )}
                    </div>

                    {/* Review Form */}
                    {showForm && (
                        <div className="review-form-container">
                            {formSuccess ? (
                                <div className="form-success">
                                    <span className="success-icon">✓</span>
                                    <h3>Thank You!</h3>
                                    <p>Your review has been submitted and will be published after moderation.</p>
                                    <button onClick={() => setFormSuccess(false)} className="btn-primary">
                                        Write Another Review
                                    </button>
                                </div>
                            ) : userInfo?.has_reviewed ? (
                                <div className="already-reviewed">
                                    <span className="info-icon">ℹ️</span>
                                    <p>You have already submitted a review. Thank you for your feedback!</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="review-form">
                                    <h3>Share Your Experience</h3>

                                    {!isLoggedIn && (
                                        <>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Your Name *</label>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleFormChange}
                                                        required
                                                        placeholder="John Doe"
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Email Address *</label>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleFormChange}
                                                        required
                                                        placeholder="john@example.com"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {isLoggedIn && userInfo?.user && (
                                        <div className="user-info-display">
                                            <p>Posting as: <strong>{userInfo.user.name}</strong> ({userInfo.user.email})</p>
                                            {userInfo.is_verified_customer && (
                                                <span className="verified-badge">✓ Verified Customer</span>
                                            )}
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label>Your Rating *</label>
                                        <div className="rating-selector">
                                            {renderStars(formData.rating, true, 'lg')}
                                            <span className="rating-text">
                                                {formData.rating === 5 && 'Excellent!'}
                                                {formData.rating === 4 && 'Good'}
                                                {formData.rating === 3 && 'Average'}
                                                {formData.rating === 2 && 'Below Average'}
                                                {formData.rating === 1 && 'Poor'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Review Title *</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleFormChange}
                                            required
                                            maxLength="255"
                                            placeholder="Summarize your experience"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Your Review *</label>
                                        <textarea
                                            name="comment"
                                            value={formData.comment}
                                            onChange={handleFormChange}
                                            required
                                            minLength="10"
                                            placeholder="Tell us about your experience with our products or services..."
                                            rows="4"
                                        ></textarea>
                                    </div>

                                    {formError && (
                                        <div className="form-error">{formError}</div>
                                    )}

                                    <button
                                        type="submit"
                                        className="submit-btn"
                                        disabled={formLoading}
                                    >
                                        {formLoading ? 'Submitting...' : 'Submit Review'}
                                    </button>
                                </form>
                            )}
                        </div>
                    )}

                    {/* Reviews List */}
                    <div className="reviews-list">
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading reviews...</p>
                            </div>
                        ) : error ? (
                            <div className="error-state">
                                <p>{error}</p>
                                <button onClick={fetchReviews} className="retry-btn">Try Again</button>
                            </div>
                        ) : reviews.length === 0 ? (
                            <div className="empty-state">
                                <span className="empty-icon">📝</span>
                                <h3>No Reviews Yet</h3>
                                <p>{isLoggedIn ? 'Be the first to share your experience!' : 'No reviews have been submitted yet.'}</p>
                                {isLoggedIn && (
                                    <button onClick={() => setShowForm(true)} className="btn-primary">
                                        Write a Review
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                {reviews.map(review => (
                                    <div key={review.id} className="review-card">
                                        <div className="review-header">
                                            <div className="reviewer-info">
                                                <div className="reviewer-avatar">
                                                    {review.name?.charAt(0)?.toUpperCase() || 'U'}
                                                </div>
                                                <div className="reviewer-details">
                                                    <span className="reviewer-name">{review.name}</span>
                                                    {review.is_verified_purchase && (
                                                        <span className="verified-badge">✓ Verified Purchase</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="review-meta">
                                                {renderStars(review.rating)}
                                                <span className="review-date">
                                                    {new Date(review.created_at).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                </span>
                                            </div>
                                        </div>
                                        <h4 className="review-title">{review.title}</h4>
                                        <p className="review-comment">{review.comment}</p>
                                    </div>
                                ))}

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="pagination">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="page-btn"
                                        >
                                            ← Previous
                                        </button>
                                        <span className="page-info">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="page-btn"
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewDialog;
